import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, InputNumber, Space, Table, Tag, message, notification } from "antd";
import { Flex } from "../../css/styles";
import { titleCase } from "../SelectSalesChannel";
import { DownloadOutlined, EditOutlined, SaveOutlined, SettingOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchInventory } from "../../api/inventory";

import { orderBy, uniqueId } from "lodash-es";
import { Search, Title, Wrapper } from "./styles";
import { getColumns, InventoryTableData, Option, redactedColumns, renderTooltip } from "./helper";
import Papa from "papaparse";
import moment from "moment";

import { ChannelStockReallocationModal } from "./ChannelStockReallocationModal";
import { Facility } from "../../api/common/facility";
import { useInventoryPreferenceStore } from "../../hooks/useInventoryPreferenceStore";
import { downloadCsv } from "api/fulfillment/downloadCSV";
import { useFacilityIndex } from "hooks/api/common/useFacilityIndex";
import { fetchInventoryChannels, updateProduct } from "api/fulfillment/product";
import { transferStock } from "api/fulfillment/inventoryChannels";
import { useProductDetails } from "hooks/api/fulfillment/products";
import { FiltersDrawer } from "components/FilterDrawer";
import { useOrganizationConfigs } from "../../hooks/api/fulfillment/organization";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlagQuery";

type ProductInventoryTableProps = {
  productId: string;
  orgId: string;
};

export function ProductInventoryTable({ productId, orgId }: ProductInventoryTableProps) {
  const { fetchProduct } = useProductDetails();
  const [search, setSearch] = useState("");
  const [isStockReallocationOpen, setIsStockReallocationOpen] = useState(false);
  const [isSettingDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
  const { facilities, setFacilities, channels, setChannels, inventories, setInventories } =
    useInventoryPreferenceStore(orgId)();

  const queryClient = useQueryClient();
  const { mutate: doDownloadCsv } = useMutation(downloadCsv);
  const { data: channelOptions = [], ...channelsQuery } = useQuery(
    ["channels", orgId],
    () => fetchInventoryChannels(orgId),
    {
      enabled: !!orgId,
      select({ data }) {
        return data.map((d) => ({ value: d.name, label: titleCase(d.name) }));
      },
    }
  );

  const useNewWarehouseInventory = useFeatureFlagQuery(
    "use-new-warehouse-inventory-values",
    { organization_id: orgId },
    { enabled: !!orgId }
  );

  const { data: facilityOptions = [] } = useFacilityIndex({
    select<Option>(data: Facility[]) {
      const facilities = data.filter((d) => d.name !== "unknown" && d.enabled);
      return orderBy(
        facilities.map((f) => ({
          value: f.name,
          label: `${f.address.city} / ${f.name.toUpperCase()}`,
        })),
        [(f) => f.value],
        ["asc"]
      ) as unknown as Option[];
    },
  }) as { data?: Option[] };

  const getFacilityLabel = (name: string) => {
    const facility = facilityOptions?.find((f) => f.value === name);
    if (!facility) return name;
    return facility.label;
  };

  const { data: inventory = [], ...inventoryQuery } = useQuery(
    ["product-inventory", productId, channels, facilities, useNewWarehouseInventory.data?.state],
    () =>
      fetchInventory({
        version: useNewWarehouseInventory.data?.state ? 2 : 1,
        product_id: productId,
        organization_id: orgId,
        collate: true,
        channels: channels,
        facilities: facilities,
      }),
    {
      enabled: !!productId,
      select(data) {
        return orderBy(data, [(o) => o.facilities[0]], ["asc"]).map((d) => ({
          ...d,
          facilities: d.facilities.map((f) => ({ value: f, label: getFacilityLabel(f) })),
        })) as InventoryTableData[];
      },
    }
  );

  const { mutate: handleTransferStock, ...mutateChannel } = useMutation(transferStock, {
    onSuccess() {
      message.success("Channel stock reallocated successfully.");
      setIsStockReallocationOpen(false);
      inventoryQuery.refetch();
    },
  });

  const { data: configs } = useOrganizationConfigs(orgId);

  const columns = useMemo(() => {
    const onSSOT = configs?.inventory_source === "internal";
    const canUpdateOnHand = !onSSOT;
    const hasChannels = !!channelOptions.length;
    const baseColumns = getColumns(canUpdateOnHand, hasChannels, !!useNewWarehouseInventory.data?.state);
    const columns = !channels.length ? baseColumns : redactedColumns(baseColumns);

    if (onSSOT) return columns;
    return columns.filter((c) => c.key !== "in_transit");
  }, [configs, channels, channelOptions, useNewWarehouseInventory.data?.state]);

  const inventoryOptions = columns
    .filter((c) => c.key !== "facility")
    .map((c) => {
      if (c.key === "in_transit") {
        return { value: c.key, label: renderTooltip(c.title, "On a GoBolt truck for delivery") };
      }
      if (c.key === "on_hand") {
        return {
          value: c.key,
          label: renderTooltip(
            c.title,
            "For organizations with Providence as the stock source of truth, on hand excludes in-progress GoBolt Last Mile / Parcel orders. "
          ),
        };
      }
      return { value: c.key, label: c.title };
    });

  const SETTINGS_OPTIONS = [
    {
      title: "Channels",
      value: channels,
      onChange: setChannels,
      options: channelOptions,
    },
    {
      title: "Warehouse",
      value: facilities,
      onChange: setFacilities,
      options: facilityOptions,
    },
    {
      title: "Inventory",
      value: inventories,
      onChange: setInventories,
      options: inventoryOptions,
    },
  ];

  const getSettingsOptions = (hasChannels = true) => {
    if (!hasChannels) {
      return SETTINGS_OPTIONS.filter((x) => x.title !== "Channels");
    }
    return SETTINGS_OPTIONS;
  };

  const handleExport = async () => {
    if (!inventory?.length) return;
    const omit = ["product_id", "reserved_orders", "orders_to_be_reserved"];
    try {
      const { sku, upc } = await queryClient.fetchQuery(["product", productId], () => fetchProduct(productId));
      const dump = inventory.map((i) => ({ sku, upc, ...i, facilities: i.facilities.map((f) => f.label) }));
      const csv = Papa.unparse({
        fields: Object.keys(dump[0]).filter((i) => !omit.includes(i)),
        data: dump,
      });
      doDownloadCsv({ blobPart: csv, filename: `${sku}_inventory_${moment().unix()}.csv` });
    } catch (error) {
      const e = error as Error | string;
      notification.error({ message: typeof e === "string" ? e : e.message });
    }
  };

  useEffect(() => {
    // Filter out invalid facilities that are not in the facility options (recently disabled)
    const validFacilities = facilities.filter((f) => facilityOptions.map((f) => f.value).includes(f));
    // Update facilities state if there are any invalid facilities
    if (validFacilities.length !== facilities.length) {
      setFacilities(validFacilities);
    }
    /*eslint-disable react-hooks/exhaustive-deps */
  }, [facilities, facilityOptions]);

  useEffect(() => {
    if (!inventories.length) setInventories(inventoryOptions.map((c) => c.value));
    if (!facilities.length) setFacilities(facilityOptions.map((f) => f.value));
    /*eslint-disable react-hooks/exhaustive-deps */
  }, [inventories.length, facilities.length, facilityOptions]);

  return (
    <Wrapper>
      <Title level={4}>INVENTORY</Title>
      <Card>
        <Flex direction="column" align="stretch" gap="1em">
          <Flex justify="space-between">
            <Space>
              <Search placeholder="Search Warehouse" defaultValue={search} onSearch={setSearch} allowClear />
              <Space size={0}>
                {channels.length >= 3 ? (
                  <Tag>{channels.length} channels</Tag>
                ) : (
                  channels.map((c) => <Tag key={c}>{titleCase(c)}</Tag>)
                )}

                {facilities.length >= 3 ? (
                  <Tag>{facilities.length} warehouses</Tag>
                ) : (
                  facilities.map((c) => <Tag key={c}>{c}</Tag>)
                )}

                {inventories.length >= 3 ? (
                  <Tag>{inventories.length} inventories</Tag>
                ) : (
                  inventories.map((c) => <Tag key={c}>{c}</Tag>)
                )}
              </Space>
            </Space>
            <Space style={{ marginLeft: "auto" }}>
              {!!channelOptions.length && (
                <Button type="link" onClick={() => setIsStockReallocationOpen(true)}>
                  Channel Stock Reallocation
                </Button>
              )}
              <Button
                icon={<SettingOutlined />}
                onClick={() => setIsSettingsDrawerOpen(true)}
                disabled={channelsQuery.isLoading}
              >
                Settings
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!inventory?.length}>
                Export
              </Button>
            </Space>
          </Flex>
          <Table<InventoryTableData>
            loading={inventoryQuery.isLoading}
            dataSource={inventory.filter((i) =>
              i.facilities
                .map((f) => f.label)
                .join("")
                .toLowerCase()
                .includes(search.toLowerCase())
            )}
            columns={columns.filter((c) => inventories.includes(c.key) || c.key === "facility")}
            rowKey={(r, i) => uniqueId(r.product_id + i)}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </Flex>
      </Card>
      <ChannelStockReallocationModal
        visible={isStockReallocationOpen}
        loading={mutateChannel.isLoading}
        orgId={orgId}
        productId={productId}
        onCancel={() => setIsStockReallocationOpen(false)}
        onSubmit={handleTransferStock}
      />
      <FiltersDrawer
        filterOptions={getSettingsOptions(!!channelOptions.length)}
        onClose={() => setIsSettingsDrawerOpen(false)}
        visible={isSettingDrawerOpen}
      />
    </Wrapper>
  );
}

/**
 * THROWAWAY CODE REMOVE ME WHEN SSOT IS DEFAULT SOURCE OF INVENTORY FOR ALL ORGS
 */
export const EditInventory: React.FC<{ inventory: Partial<InventoryTableData> }> = ({ inventory }) => {
  const { on_hand = 0, facilities = [], product_id = "" } = inventory;
  const queryClient = useQueryClient();
  const { fetchProduct } = useProductDetails();
  const [isEditing, setIsEditing] = useState(false);
  const [onHand, setOnHand] = useState(on_hand);

  const productMutation = useMutation(updateProduct, {
    onSuccess() {
      message.success("Inventory updated successfully.");
      queryClient.refetchQueries("product-inventory", { active: true });
      queryClient.refetchQueries("product-activity-history");
      setIsEditing(false);
    },
  });

  const productQuery = useQuery(["product", product_id], () => fetchProduct(product_id), { enabled: !!product_id });

  if (productQuery.data?.product_type === "kit") return <>{onHand}</>;
  return (
    <Space size={2}>
      <InputNumber
        size="small"
        min={0}
        value={onHand}
        onChange={setOnHand}
        readOnly={!isEditing}
        bordered={isEditing}
        onDoubleClick={() => setIsEditing(true)}
      />

      {isEditing ? (
        <Button
          size="small"
          type="primary"
          icon={<SaveOutlined />}
          loading={productMutation.isLoading}
          disabled={!product_id}
          onClick={() =>
            productMutation.mutate({
              productId: product_id,
              body: { product: { stocks: facilities?.map((f) => ({ facility: f.value, quantity: onHand })) } },
            })
          }
          title="Save On Hand"
        />
      ) : (
        <Button
          size="small"
          type="text"
          icon={<EditOutlined />}
          onClick={() => setIsEditing(true)}
          title="Edit On Hand"
        />
      )}
    </Space>
  );
};
